/**
 * CollapsiblePanelEditor
 *
 * Property editor for collapsible-panel control type
 * Configures panel expansion, animation, header styling, and state
 *
 * SRP: Responsible ONLY for configuring collapsible-panel properties
 * Control Type: 'collapsible-panel'
 *
 * Properties Configured (8):
 * 1. headerText - Panel header text
 * 2. headerBackgroundColor - Header background color
 * 3. headerTextColor - Header text color
 * 4. isExpandedByDefault - Initial expansion state
 * 5. animationDuration - Expand/collapse speed (ms)
 * 6. borderColor - Panel border color
 * 7. iconPosition - Icon placement (left/right)
 * 8. disableAnimation - Disable expand/collapse animation
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { TextPropertyEditor } from '../BaseEditors/TextPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';

/**
 * Collapsible panel configuration
 */
interface CollapsiblePanelConfig {
  headerText?: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  isExpandedByDefault?: boolean;
  animationDuration?: number;
  borderColor?: string;
  iconPosition?: 'left' | 'right';
  disableAnimation?: boolean;
}

/**
 * CollapsiblePanelEditor Component
 * Renders property editors for all collapsible-panel configuration
 */
const CollapsiblePanelEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as CollapsiblePanelConfig) || {},
    [control.config],
  );

  const handleHeaderTextChange = useCallback(
    (value: string) => onConfigChange('headerText', value),
    [onConfigChange],
  );

  const handleHeaderBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('headerBackgroundColor', value),
    [onConfigChange],
  );

  const handleHeaderTextColorChange = useCallback(
    (value: string) => onConfigChange('headerTextColor', value),
    [onConfigChange],
  );

  const handleIsExpandedByDefaultChange = useCallback(
    (value: boolean) => onConfigChange('isExpandedByDefault', value),
    [onConfigChange],
  );

  const handleAnimationDurationChange = useCallback(
    (value: number) => onConfigChange('animationDuration', value),
    [onConfigChange],
  );

  const handleBorderColorChange = useCallback(
    (value: string) => onConfigChange('borderColor', value),
    [onConfigChange],
  );

  const handleIconPositionChange = useCallback(
    (value: string) => onConfigChange('iconPosition', value as 'left' | 'right'),
    [onConfigChange],
  );

  const handleDisableAnimationChange = useCallback(
    (value: boolean) => onConfigChange('disableAnimation', value),
    [onConfigChange],
  );

  const iconPositionOptions = useMemo(
    () => [
      { label: 'Left', value: 'left' },
      { label: 'Right', value: 'right' },
    ],
    [],
  );

  return (
    <div className="af-property-editor__section collapsible-panel-editor">
      <h3 className="af-property-editor__section-title">Collapsible Panel Properties</h3>

      <TextPropertyEditor
        label="Header Text"
        value={config.headerText || ''}
        onChange={handleHeaderTextChange}
        placeholder="Enter panel header"
        helpText="Text displayed in the panel header"
      />

      <ColorPropertyEditor
        label="Header Background Color"
        value={config.headerBackgroundColor || '#F5F5F5'}
        onChange={handleHeaderBackgroundColorChange}
        helpText="Background color for the header"
      />

      <ColorPropertyEditor
        label="Header Text Color"
        value={config.headerTextColor || '#000000'}
        onChange={handleHeaderTextColorChange}
        helpText="Text color for the header"
      />

      <CheckboxPropertyEditor
        label="Expanded by Default"
        value={config.isExpandedByDefault || false}
        onChange={handleIsExpandedByDefaultChange}
        helpText="Panel is expanded when form loads"
      />

      <NumberPropertyEditor
        label="Animation Duration (ms)"
        value={config.animationDuration || 300}
        onChange={handleAnimationDurationChange}
        min={0}
        max={2000}
        step={100}
        helpText="Duration of expand/collapse animation"
      />

      <ColorPropertyEditor
        label="Border Color"
        value={config.borderColor || '#E0E0E0'}
        onChange={handleBorderColorChange}
        helpText="Panel border color"
      />

      <SelectPropertyEditor
        label="Icon Position"
        value={config.iconPosition || 'left'}
        onChange={handleIconPositionChange}
        options={iconPositionOptions}
        helpText="Position of the expand/collapse icon"
      />

      <CheckboxPropertyEditor
        label="Disable Animation"
        value={config.disableAnimation || false}
        onChange={handleDisableAnimationChange}
        helpText="Expand/collapse instantly without animation"
      />
    </div>
  );
};

CollapsiblePanelEditorComponent.displayName = 'CollapsiblePanelEditor';

/**
 * Collapsible Panel Property Editor Registration
 */
export const CollapsiblePanelEditor: PropertyEditor = {
  type: 'collapsible-panel',
  label: 'Collapsible Panel',
  description: 'Configure panel expansion, animation, header styling, and state',
  version: '1.0.0',
  render: (props) => <CollapsiblePanelEditorComponent {...props} />,
};

export default CollapsiblePanelEditor;
